import { useEffect, useRef, useState } from "react";
import tw from "tailwind-styled-components";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { MdClose } from "react-icons/md";
import { FaRegCalendar } from "react-icons/fa";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
    RupeeIcon,
    ModalHeader,
} from "../Styles/InputStyles";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { paymentModes } from "../../helpers";

const getValidationSchema = (inhandAmount) =>
    Yup.object().shape({
        collectionDate: Yup.string()
            .required("Collection date is required")
            .test(
                "not-in-future",
                "Collection date cannot be in the future",
                (value) =>
                    !value || moment(value).isSameOrBefore(moment(), "day")
            ),
        amount: Yup.number()
            .typeError("Amount must be a number")
            .required("Amount is required")
            .moreThan(0, "Amount must be greater than 0")
            .test(
                "max-inhand",
                `Amount cannot exceed inhand ₹${Number(
                    inhandAmount || 0
                ).toLocaleString("en-IN")}`,
                (value) =>
                    value === undefined ||
                    value === null ||
                    Number(value) <= Number(inhandAmount || 0)
            ),
        mode: Yup.number()
            .typeError("Payment mode is required")
            .required("Payment mode is required"),
    });

const CashRecordModal = ({
    handleClose,
    payToClientMutate,
    payToClientLoading,
    staffId,
    inhandAmount,
}) => {
    const [calendarOpen, setCalendarOpen] = useState(false);
    const formikRef = useRef(null);
    const [height, setHeight] = useState(window.innerHeight);

    useEffect(() => {
        const handleResize = () => setHeight(window.innerHeight);
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const initialValues = {
        collectionDate: moment().format("YYYY-MM-DD"),
        amount: inhandAmount || "",
        mode: "",
    };

    const validationSchema = getValidationSchema(inhandAmount);

    const handleSubmit = (values) => {
        const payload = {
            staffId: staffId,
            amount: values.amount,
            collectionDate: moment(values.collectionDate).utc().format(),
            mode: values.mode || 0,
            paymentId: 1,
        };
        payToClientMutate(payload);
    };

    return (
        <Container
            style={{
                height: `${height}px`,
                overflow: "auto",
                WebkitOverflowScrolling: "touch",
            }}
        >
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">
                    Record Cash
                </h1>
                <button onClick={handleClose} type="button">
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <div className="px-6 flex flex-col justify-between">
                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                    validateOnChange
                    validateOnBlur
                >
                    {({ setFieldValue, values, resetForm, errors, touched }) => (
                        <Form>
                            <ScrollableContent className="!max-h-[calc(100dvh-140px)]">
                                <Wrapper>
                                    <InputGroup>
                                        <Label htmlFor="collectionDate">
                                            Collection Date *
                                        </Label>
                                        <FieldWrappers className="relative w-full">
                                            <DatePicker
                                                selected={
                                                    values.collectionDate
                                                        ? moment(
                                                            values.collectionDate,
                                                            "YYYY-MM-DD"
                                                        ).toDate()
                                                        : null
                                                }
                                                onChange={(date) =>
                                                    setFieldValue(
                                                        "collectionDate",
                                                        moment(date).format(
                                                            "YYYY-MM-DD"
                                                        )
                                                    )
                                                }
                                                maxDate={new Date()}
                                                dateFormat="yyyy-MM-dd"
                                                className="cursor-pointer"
                                                placeholderText="YYYY-MM-DD"
                                                onClickOutside={() =>
                                                    setCalendarOpen(false)
                                                }
                                                onSelect={() =>
                                                    setCalendarOpen(false)
                                                }
                                                open={calendarOpen}
                                                onInputClick={() =>
                                                    setCalendarOpen(true)
                                                }
                                                popperPlacement="top-end"
                                                showIcon
                                                icon={
                                                    <FaRegCalendar className="text-gray-400 text-xs mt-0.5" />
                                                }
                                                showPopperArrow={false}
                                                onFocus={(e) =>
                                                    e.target.blur()
                                                }
                                                wrapperClassName="w-full"
                                                portalId="root-portal"
                                                popperClassName="react-datepicker-popper"
                                            />
                                        </FieldWrappers>
                                        {errors.collectionDate &&
                                            touched.collectionDate && (
                                                <ErrorText>
                                                    {errors.collectionDate}
                                                </ErrorText>
                                            )}
                                    </InputGroup>

                                    <InputGroup>
                                        <Label htmlFor="amount">
                                            Amount *
                                        </Label>
                                        <FieldWrappers className="w-full">
                                            <RupeeIcon />
                                            <Field
                                                type="number"
                                                name="amount"
                                                id="amount"
                                                placeholder="Enter amount"
                                                className="w-full pl-5"
                                                onWheel={(e) =>
                                                    e.target.blur()
                                                }
                                            />
                                        </FieldWrappers>
                                        {errors.amount && touched.amount && (
                                            <ErrorText>
                                                {errors.amount}
                                            </ErrorText>
                                        )}
                                    </InputGroup>

                                    <InputGroup>
                                        <Label>Payment Mode *</Label>
                                        <PaymentModeWrapper>
                                            {paymentModes.map((mode) => (
                                                <PaymentModeCard
                                                    key={mode.value}
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setFieldValue(
                                                            "mode",
                                                            mode.value,
                                                            true // ← mark touched
                                                        );
                                                    }}
                                                    className={
                                                        values.mode ===
                                                            mode.value
                                                            ? "bg-blue-100 border-blue-500"
                                                            : "bg-gray-100"
                                                    }
                                                >
                                                    <span className="text-sm">
                                                        {mode.label}
                                                    </span>
                                                </PaymentModeCard>
                                            ))}
                                        </PaymentModeWrapper>
                                        {errors.mode && touched.mode && (
                                            <ErrorText>
                                                {errors.mode}
                                            </ErrorText>
                                        )}
                                    </InputGroup>
                                </Wrapper>
                            </ScrollableContent>

                            <ActionSection>
                                <SideCancelBtn
                                    type="button"
                                    onClick={() => {
                                        resetForm();
                                        handleClose();
                                    }}
                                >
                                    Cancel
                                </SideCancelBtn>
                                <SubmitBtn
                                    type="submit"
                                    disabled={payToClientLoading}
                                >
                                    {payToClientLoading
                                        ? "Submitting..."
                                        : "Submit"}
                                </SubmitBtn>
                            </ActionSection>
                        </Form>
                    )}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const PaymentModeWrapper = tw.div`grid grid-cols-4 gap-2 my-1.5`;
const PaymentModeCard = tw.button`flex flex-col items-center justify-center p-2 border rounded-lg shadow-sm cursor-pointer`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5 mt-1`;

export default CashRecordModal;